import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { requestMotionPermission } from "@/hooks";
import {
  readShakeSpotlightPermissionState,
  writeShakeSpotlightPermissionState,
  type MotionPermissionState,
} from "@/lib/shake-spotlight-permission";

export const ShakePermissionSection: React.FC = () => {
  const { t } = useTranslation();
  const [permissionState, setPermissionState] =
    useState<MotionPermissionState>("unknown");
  const [userEnabled, setUserEnabled] = useState(false);

  const isSupported = typeof window !== "undefined" && "DeviceMotionEvent" in window;
  const needsPermission = useMemo(() => {
    if (typeof window === "undefined") return false;
    // iOS Safari: DeviceMotionEvent.requestPermission mevcut olabilir
    return typeof (DeviceMotionEvent as any)?.requestPermission === "function";
  }, []);

  // Persisted state (refresh sonrası doğru görünmesi için)
  useEffect(() => {
    const persisted = readShakeSpotlightPermissionState();
    setUserEnabled(persisted.userEnabled);
    setPermissionState(persisted.motionPermission);
  }, []);

  const statusTextKey = useMemo(() => {
    if (!isSupported) return "settings.permissions.shake.status.notSupported";
    if (!userEnabled) return "settings.permissions.shake.status.disabled";
    if (!needsPermission) return "settings.permissions.shake.status.enabled";
    if (permissionState === "unknown") return "settings.permissions.shake.status.unknown";
    if (permissionState === "granted")
      return "settings.permissions.shake.status.enabled";
    if (permissionState === "denied")
      return "settings.permissions.shake.status.denied";
    return "settings.permissions.shake.status.prompt";
  }, [isSupported, needsPermission, permissionState, userEnabled]);

  const handlePrimaryAction = async () => {
    // Requirement: Kullanıcı önce buradan manuel olarak aktif etmeli
    setUserEnabled(true);
    writeShakeSpotlightPermissionState({ userEnabled: true });

    // iOS gibi permission gereken ortamlarda ayrıca permission iste
    if (needsPermission) {
      const result = await requestMotionPermission();
      setPermissionState(result);
      writeShakeSpotlightPermissionState({ motionPermission: result });
    } else {
      // Permission gerekmiyorsa "enabled" olarak kabul ediyoruz.
      setPermissionState("granted");
      writeShakeSpotlightPermissionState({ motionPermission: "granted" });
    }
  };

  const handleDisable = () => {
    setUserEnabled(false);
    writeShakeSpotlightPermissionState({ userEnabled: false });
  };

  const actionLabelKey = useMemo(() => {
    if (!isSupported) return "settings.permissions.shake.action.notSupported";
    if (userEnabled) return "settings.permissions.shake.action.disable";
    if (needsPermission) return "settings.permissions.shake.action.request";
    return "settings.permissions.shake.action.enable";
  }, [isSupported, needsPermission, userEnabled]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h4 className="text-sm font-semibold text-text-secondary dark:text-text-secondary">
            {t("settings.permissions.shake.title")}
          </h4>
          <p className="text-sm text-text-tertiary dark:text-text-tertiary">
            {t("settings.permissions.shake.description")}
          </p>
        </div>
      </div>

      {/* Status + Action (tek satır) */}
      <div className="flex items-center justify-between gap-3 rounded-lg border border-border-secondary dark:border-border-secondary bg-bg-secondary dark:bg-bg-secondary px-4 py-3">
        <p className="text-sm text-text-tertiary dark:text-text-tertiary truncate">
          <span className="font-medium text-text-secondary dark:text-text-secondary">
            {t("settings.permissions.shake.statusLabel")}
          </span>{" "}
          {t(statusTextKey)}
        </p>

        <div className="flex items-center gap-2 shrink-0">
          {userEnabled && (
            <Button type="button" variant="ghost" onClick={handleDisable}>
              {t("settings.permissions.shake.action.disable")}
            </Button>
          )}
          {!userEnabled && (
            <Button
              type="button"
              variant="secondary"
              onClick={handlePrimaryAction}
              disabled={!isSupported}
            >
              {t(actionLabelKey)}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};


